import withStyles from '../components/jss'
import { useState, useCallback, useEffect, useMemo, useRef } from 'preact/hooks'
import { Link } from 'react-router-dom'

import { submitFlag, getSolves } from '../api/challenges'
import { useToast } from './toast'
import Markdown from './markdown'
import Ellipsis from '../icons/ellipsis-vertical-solid.svg'
import Flag from '../icons/flag-regular.svg'
import Star from '../icons/star-regular.svg'
import Plane from '../icons/paper-plane-regular.svg'
import Right from '../icons/chevron-right-solid.svg'
import Up from '../icons/chevron-up-solid.svg'
import { formatRelativeTime } from '../util/time'

const ExternalLink = props => <a {...props} target='_blank' />

const markdownComponents = {
  A: ExternalLink,
}

const solvesPageSize = 100

const BrainrotProblem = ({
  classes,
  problem,
  solved,
  setSolved,
  challengeVideos,
  admin,
  muted,
}) => {
  const { toast } = useToast()

  const hasDownloads = problem.files.length !== 0

  const [error, setError] = useState(undefined)
  const hasError = error !== undefined

  const [value, setValue] = useState('')
  const handleInputChange = useCallback(e => setValue(e.target.value), [])

  const handleSubmit = useCallback(
    e => {
      e.preventDefault()

      submitFlag(problem.id, value.trim()).then(({ error }) => {
        if (error === undefined) {
          toast({ body: 'Flag successfully submitted!' })

          setSolved(problem.id)
        } else {
          toast({ body: error, type: 'error' })
          setError(error)
        }
      })
    },
    [toast, setSolved, problem, value]
  )

  const [solves, setSolves] = useState(null)
  const [solvesPending, setSolvesPending] = useState(false)
  const modalRef = useRef(null)
  const [modalOpen, setModalOpen] = useState(false)
  const onSolvesClick = useCallback(
    async e => {
      e.preventDefault()
      if (solvesPending) {
        return
      }
      setSolvesPending(true)
      let solves = []
      let offset = 0
      while (true) {
        const { kind, message, data } = await getSolves({
          challId: problem.id,
          limit: solvesPageSize,
          offset: offset,
        })
        if (kind !== 'goodChallengeSolves') {
          if (admin) {
            break
          }
          setSolvesPending(false)
          toast({ body: message, type: 'error' })
          return
        }
        solves = solves.concat(data.solves)
        offset += solvesPageSize
        if (data.solves.length < solvesPageSize) {
          break
        }
      }
      setSolvesPending(false)
      setSolves(solves)
      modalRef.current?.showModal()
      setModalOpen(true)
    },
    [problem.id, toast, solvesPending, admin]
  )

  const onSolvesClose = useCallback(() => {
    setSolves(null)
    modalRef.current?.close()
    setModalOpen(false)
  }, [])

  const onVideoClick = useCallback(async e => {
    if (e.target.paused) {
      await e.target.play()
    } else {
      e.target.pause()
    }
  }, [])

  const [descriptionOpen, setDescriptionOpen] = useState(false)

  const toggleDescription = useCallback(
    e => setDescriptionOpen(!descriptionOpen),
    [descriptionOpen]
  )

  const video = useMemo(() => {
    if (!challengeVideos) {
      return null
    }
    if (problem.category in challengeVideos) {
      if (problem.id in challengeVideos[problem.category]) {
        return challengeVideos[problem.category][problem.id]
      }
    }
    return null
  }, [challengeVideos, problem])

  const videoRef = useRef(null)

  const pfp = useCallback(e => {
    if (e === 'aparker') return ['aparker.webp']
    if (e === 'cephi') return ['cephi.webp']
    if (e === 'clam') return ['clam.webp']
    if (e === 'esiddali') return ['Ishaan514.webp']
    if (e === 'evilmuffinha') return ['emh.webp']
    if (e === 'evilmuffinha + clam') return ['emh.webp', 'clam.webp']
    if (e === 'relkochta') return ['relkochta.webp']
    if (e === 'segal') return ['segal.webp']
    if (e === 'tahmid-23') return ['tahmid23.webp']
    if (e === 'triacontakai') return ['triacontakai.webp']
    if (e === 'unsure') return ['unsure.png']
    return ['pfp.png']
  }, [])

  console.log(problem.author)

  const hasBorder = useMemo(() => Math.random() < 0.5, [])

  useEffect(() => {
    videoRef.current?.load()
  }, [video])

  function handleKeyDown(e) {
    if (e.key === 'Escape') {
      onSolvesClose()
    }
  }

  return (
    <div className={classes.challContainer}>
      <div className={classes.chall}>
        {problem.name === 'unfinished' && (
          <div
            style={{
              position: 'absolute',
              height: '100%',
              width: '100%',
              backgroundColor: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <img
              style={{
                width: '128px',
                marginBottom: '96px',
              }}
              src='/404.gif'
              alt='a very real 404'
            />
          </div>
        )}
        {video && (
          <video
            className={classes.video}
            onClick={onVideoClick}
            loop
            muted={muted}
            playsinline
            ref={videoRef}
          >
            <source src={video} type='video/mp4' />
            Your browser does not support the video tag.
          </video>
        )}
        <div
          className={`${classes.meta}
          ${solved ? classes.solved : classes.unsolved}`}
        >
          <div className={classes.details}>
            <div className={classes.author}>
              {pfp(problem.author).length > 1 ? (
                <div
                  style={{ marginRight: '1.25rem' }}
                  className={classes.pfpWrapper}
                >
                  <img src='/circle.png' />
                  <img className={classes.pfpIcon} src='/pfp/clam.webp' />
                  <img
                    style={{ left: '1.7rem' }}
                    className={classes.pfpIcon}
                    src='/pfp/emh.webp'
                  />
                </div>
              ) : (
                <div className={classes.pfpWrapper}>
                  {problem.author === 'clam' || hasBorder ? (
                    <img src='/circle.png' />
                  ) : (
                    <></>
                  )}
                  <img
                    className={classes.pfpIcon}
                    src={`/pfp/${pfp(problem.author)[0]}`}
                  />
                </div>
              )}
              <div className={classes.name}>{problem.author}</div>
              <div className={classes.follow}>Follow</div>
            </div>
            <div className={classes.detailsInner}>
              {descriptionOpen && (
                <>
                  <div className={classes.markup}>
                    <Markdown
                      content={problem.description}
                      components={markdownComponents}
                    />
                  </div>
                  {hasDownloads && (
                    <div>
                      <div className='tag-container'>
                        {problem.files.map(file => {
                          return (
                            <div
                              className={`
                                tag ${classes.tag} ${classes.download}
                              `}
                              key={file.url}
                            >
                              <a download href={`${file.url}`}>
                                {file.name}
                              </a>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )}
                </>
              )}
              <div
                className={classes.descriptionSummary}
                onClick={toggleDescription}
              >
                {descriptionOpen ? (
                  <div style={{ width: '1rem', marginRight: '.25rem' }}>
                    <Up />
                  </div>
                ) : (
                  <div
                    style={{
                      width: '.6rem',
                      marginLeft: '.2rem',
                      marginRight: '.45rem',
                    }}
                  >
                    <Right />
                  </div>
                )}
                {problem.category}/{problem.name}
                {!descriptionOpen && <span>...</span>}
              </div>
            </div>
          </div>
          <div className={classes.icons} onClick={onSolvesClick}>
            <div className={classes.image}>
              <Star />
            </div>
            {problem.points}
            {problem.points === 1 ? ' point' : ' points'}
            <div className={classes.image}>
              <Flag />
            </div>
            {problem.solves || 0}
            {problem.solves === 1 ? ' solve' : ' solves'}
            <div className={classes.image}>
              <Ellipsis />
            </div>
          </div>
        </div>
      </div>
      <dialog
        className={`${classes.modal} ${modalOpen && 'open'}`}
        ref={modalRef}
        onKeyDown={handleKeyDown}
      >
        <div className={classes.modalBackground} onClick={onSolvesClose} />
        <div className={classes.modalWrapper}>
          <div className={classes.modalBackground} onClick={onSolvesClose} />
          <div className={classes.modalBox}>
            <div className={classes.commentsTitle}>Solves</div>
            <div className={classes.commentsSolves}>
              {solves &&
                solves.map((solve, i) => (
                  <div key={solve.userId} className={classes.commentsSolve}>
                    <div className={classes.pfpWrapper}>
                      {Math.random() < 0.33 ? <img src='/circle.png' /> : <></>}
                      <img className={classes.pfpIcon} src='/pfp/pfp.png' />
                    </div>
                    <div className={classes.solve}>
                      <div>
                        <Link to={`/profile/${solve.userId}`}>
                          {solve.userName}
                        </Link>
                      </div>
                      <div>{formatRelativeTime(solve.createdAt)}</div>
                    </div>
                  </div>
                ))}
            </div>
            <form className={classes.commentsSubmit} onSubmit={handleSubmit}>
              <div className={classes.pfpWrapper}>
                <img src='/circle.png' />
                <img className={classes.pfpIcon} src='/pfp/pfp.png' />
              </div>
              <input
                type='text'
                autoComplete='off'
                autoCorrect='off'
                value={value}
                onChange={handleInputChange}
                placeholder={
                  solved
                    ? `You solved ${problem.name}!`
                    : `Submit a flag for ${problem.name}`
                }
                className={`${hasError ? 'input-error' : ''}
                  ${solved ? 'input-success' : ''}`}
              />
              <button className={classes.flagSubmit}>
                <Plane />
              </button>
            </form>
          </div>
        </div>
      </dialog>
    </div>
  )
}

export default withStyles(
  {
    '@keyframes slideIn': {
      from: { height: '146px' },
      to: { height: '90%' },
    },
    challContainer: {
      height: '96%',
      width: '100%',
      scrollSnapAlign: 'start',
      scrollSnapStop: 'always',
      paddingTop: '1rem',
      paddingBottom: '0',
    },
    chall: {
      position: 'relative',
      margin: 'auto',
      height: '100%',
      width: '100%',
      overflow: 'hidden',
      borderRadius: '1rem',
      backgroundColor: 'gray',
      color: 'white',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'end',
    },
    video: {
      position: 'absolute',
      minHeight: '100%',
      minWidth: '100%',
      objectFit: 'cover',
    },
    meta: {
      display: 'flex',
      flexDirection: 'row',
      padding: '1rem',
      paddingTop: '3rem',
      zIndex: '1',
    },
    details: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'end',
      flexGrow: '1',
      fontSize: '1.125rem',
    },
    detailsInner: {
      display: 'flex',
      flexFlow: 'column nowrap',
      justifyContent: 'end',
      '& summary::marker': {
        content: '"\u2BC8\u200A"',
      },
      '&[open] summary::marker': {
        content: '"\u2BC5\u200A"',
      },
    },
    author: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
    },
    name: {
      fontWeight: 'bold',
      marginLeft: '.5rem',
      marginRight: '.5rem',
    },
    follow: {
      border: '1px solid white',
      borderRadius: '.25rem',
      paddingLeft: '.5rem',
      paddingRight: '.5rem',
    },
    descriptionSummary: {
      display: 'flex',
      flexDirection: 'row',
      // alignItems: 'center',
      cursor: 'pointer',
      order: '2',
      '&:focus': {
        boxShadow: 'none',
        outline: 'none',
      },
    },
    markup: {
      maxWidth: '32rem',
    },
    download: {
      backgroundColor: '#111111',
    },
    icons: {
      display: 'flex',
      flexDirection: 'column',
      maxWidth: 'max-content',
      alignItems: 'center',
      cursor: 'pointer',
      justifyContent: 'flex-end',
      flexShrink: '0',
    },
    image: {
      '& svg': {
        marginTop: '1rem',
        marginBottom: '-.4rem',
        height: '1.5rem',
      },
    },
    modal: {
      display: 'none',
      backgroundColor: 'transparent',
      border: 'none',
      height: '100%',
      maxHeight: '100vh',
      width: '100vw',
      maxWidth: '100vw',
      justifyContent: 'center',
      overflow: 'hidden',
      padding: 0,
      '&.open': {
        display: 'flex',
      },
      '&.open div div:nth-child(2)': {
        animationPlayState: 'running',
      },
    },
    '@media screen and (min-width: 1000px)': {
      challContainer: {
        paddingBottom: '.5rem',
      },
      modal: {
        width: 'calc(100% - 320px)',
        marginLeft: '320px',
      },
    },
    modalBackground: {
      position: 'absolute',
      height: '100%',
      width: '100%',
    },
    modalWrapper: {
      height: '100%',
      aspectRatio: '9/15.9',
      maxWidth: '100vw',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'end',
    },
    modalBox: {
      height: '90%',
      maxHeight: '621px',
      width: '100%',
      backgroundColor: '#25292e',
      color: 'white',
      borderRadius: '1rem 1rem 0 0',
      padding: '1rem',
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      zIndex: 1,
      animation: '$slideIn 0.3s ease-out paused',
    },
    commentsTitle: {
      fontSize: '1.25rem',
      width: 'max-content',
      margin: 'auto',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      '&::before': {
        content: '""',
        width: '40px',
        border: '2px solid #959da6',
        borderRadius: '1rem',
        marginBottom: '.5rem',
      },
    },
    commentsSolves: {
      flexGrow: 1,
      display: 'flex',
      flexDirection: 'column',
      overflowY: 'auto',
    },
    commentsSolve: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      marginTop: '.25rem',
      marginBottom: '.25rem',
    },
    solve: {
      marginLeft: '.5rem',
      lineHeight: '1.2rem',
      '& div:last-child': {
        fontSize: '.9rem',
      },
    },
    commentsSubmit: {
      marginTop: '1rem',
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      '& input': {
        flexGrow: 1,
        backgroundColor: 'transparent',
        border: '0px solid black !important',
        color: 'white !important',
        margin: '.5rem',
        padding: '.5rem !important',
      },
      '& input:focus': {
        boxShadow: 'none !important',
        outline: 'none !important',
      },
    },
    pfpWrapper: {
      position: 'relative',
      height: '3rem',
      minHeight: '3rem',
      width: '3rem',
      minWidth: '3rem',
    },
    pfpIcon: {
      position: 'absolute',
      height: '2.5rem',
      width: '2.5rem',
      top: '.25rem',
      left: '.25rem',
      borderRadius: '50%',
      overflow: 'hidden',
    },
    flagSubmit: {
      backgroundColor: 'transparent',
      border: 'none',
      margin: 0,
      padding: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      aspectRatio: 1,
      '&:hover': {
        backgroundColor: '#38bdf8',
      },
      '& svg': {
        height: '1.5rem',
        width: '1.5rem',
      },
    },
    solved: {
      backgroundImage:
        'linear-gradient(to bottom, #00000000, #22c55e80, #22c55eb0)',
    },
    unsolved: {
      backgroundImage:
        'linear-gradient(to bottom, #00000000, #ef444480, #ef4444b0)',
    },
  },
  BrainrotProblem
)
